import { default as React } from 'react';
import { Button, Tooltip } from '@material-ui/core';
import VpnLockIcon from '@material-ui/icons/VpnLock';
import LockIcon from '@material-ui/icons/Lock';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import type { Entity } from '@backstage/catalog-model';
import { MicroappLaunchUrl, isAccessAppColumn } from './hooks';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';

/**
 * "Access App" button. Opens production when the app has been promoted — the
 * catalog entity of a promoted app carries the staging URL.
 */
const AccessAppButton = ({
  entity,
  url,
  label,
}: {
  entity: Entity;
  url: string;
  label: string;
}) => (
  <MicroappLaunchUrl entity={entity} url={url}>
    {href =>
      href ? (
        <Button
          variant="contained"
          color="primary"
          size="small"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textTransform: 'none',
            fontSize: '0.75rem',
            padding: '4px 8px',
            minWidth: 'auto',
          }}
        >
          {label}
        </Button>
      ) : null
    }
  </MicroappLaunchUrl>
);
const MICROAPP_ANNOTATION_VALUE = 'microapp';

/**
 * Helper to extract the Entity from a CatalogTableRow.
 * In Backstage v1.33+, render functions receive CatalogTableRow ({ entity, resolved }).
 */
function getEntity(rowData: any) {
  if (rowData?.entity?.metadata) {
    return rowData.entity;
  }
  if (rowData?.metadata) {
    return rowData;
  }
  return null;
}

// Map human-readable column names to their field identifiers used in Backstage v1.33+.
// Column titles are now React elements (i18n), so we must match by field instead.
const COLUMN_NAME_TO_FIELDS: Record<string, string[]> = {
  Name: ['resolved.entityRef', 'resolved.name', 'entity.metadata.name'],
  Title: ['entity.metadata.title'],
  System: ['resolved.partOfSystemRelationTitle'],
  Owner: ['resolved.ownedByRelationsTitle'],
  Lifecycle: ['entity.spec.lifecycle'],
  Type: ['entity.spec.type'],
  Tags: ['entity.metadata.tags'],
  Description: ['entity.metadata.description'],
  Namespace: ['entity.metadata.namespace'],
};

function shouldHideColumn(col: any, hideColumns: string[]): boolean {
  const field = typeof col.field === 'string' ? col.field : '';
  for (const name of hideColumns) {
    const fields = COLUMN_NAME_TO_FIELDS[name];
    if (fields && fields.some(f => f === field)) {
      return true;
    }
    // Fallback: if col.title is a plain string, match directly
    if (typeof col.title === 'string' && col.title === name) {
      return true;
    }
  }
  return false;
}

/**
 * Creates a custom columns function for catalog tables
 * @param hideColumns Array of column names to hide (e.g. 'System', 'Tags')
 * @returns CatalogTableColumnsFunc
 */
export const createColumnsFunc =
  (hideColumns: string[] = []): CatalogTableColumnsFunc =>
  entityListContext => {
    const defaultColumns = CatalogTable.defaultColumnsFunc(entityListContext);
    const updatedColumns = defaultColumns.filter(
      col => !shouldHideColumn(col, hideColumns),
    );

    // Add custom columns from entity annotations
    const customColumns: any[] = [];

    // Check if any entities have custom column annotations
    const entities = entityListContext?.entities || [];
    const sampleEntity = entities.find((entity: any) => {
      return (
        entity?.metadata?.annotations?.['backstage.io/columns'] &&
        entity?.metadata?.annotations?.[MICROAPP_ANNOTATION_KEY]
      );
    });

    if (sampleEntity) {
      try {
        const columnsAnnotation =
          sampleEntity.metadata?.annotations?.['backstage.io/columns'];
        if (columnsAnnotation) {
          const cleanedAnnotation = columnsAnnotation
            .replace(/\n\s*/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          const columnDefs = JSON.parse(cleanedAnnotation);

          // Calculate equal width for all custom columns using calc() pattern
          const percentage = 100 / columnDefs.length;
          const columnWidth = `calc(${percentage.toFixed(4)}% + 0px)`;

          // Create a column for each definition in the annotation
          columnDefs.forEach((colDef: any, index: number) => {
            if (colDef.title && colDef.type) {
              customColumns.push({
                title: colDef.title,
                field: `custom-column-${index}`,
                width: columnWidth,
                cellStyle: {
                  boxSizing: 'border-box',
                },
                headerStyle: {
                  boxSizing: 'border-box',
                },
                render: (rowData: CatalogTableRow) => {
                  try {
                    const actualEntity = getEntity(rowData);
                    if (!actualEntity?.metadata?.annotations) {
                      return null;
                    }

                    // Only show for quick projects
                    const isQuickProject =
                      actualEntity.metadata.annotations[
                        MICROAPP_ANNOTATION_KEY
                      ] === MICROAPP_ANNOTATION_VALUE;
                    if (!isQuickProject) {
                      return null;
                    }

                    // Parse the columns annotation for this entity
                    const entityColumnsAnnotation =
                      actualEntity.metadata.annotations['backstage.io/columns'];
                    if (entityColumnsAnnotation) {
                      try {
                        const cleanedEntityAnnotation = entityColumnsAnnotation
                          .replace(/\n\s*/g, ' ')
                          .replace(/\s+/g, ' ')
                          .trim();

                        const entityCols = JSON.parse(cleanedEntityAnnotation);
                        const matchingCol = entityCols.find(
                          (c: any) =>
                            c.title &&
                            c.title.replace(/\s+/g, ' ').trim() ===
                              colDef.title.replace(/\s+/g, ' ').trim(),
                        );

                        if (matchingCol && matchingCol.value) {
                          if (
                            colDef.type === 'url' &&
                            isAccessAppColumn(colDef.title)
                          ) {
                            return (
                              <AccessAppButton
                                entity={actualEntity}
                                url={matchingCol.value}
                                label={
                                  matchingCol.linkText ||
                                  colDef.linkText ||
                                  'Access App'
                                }
                              />
                            );
                          }
                          if (colDef.type === 'url') {
                            return (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                href={matchingCol.value}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  padding: '4px 8px',
                                  minWidth: 'auto',
                                }}
                              >
                                {matchingCol.linkText ||
                                  colDef.linkText ||
                                  'Access App'}
                              </Button>
                            );
                          } else if (colDef.type === 'date') {
                            // Format date for display
                            try {
                              const date = new Date(matchingCol.value);
                              return (
                                <span>
                                  {date.toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                              );
                            } catch {
                              return <span>{matchingCol.value}</span>;
                            }
                          }
                          return <span>{matchingCol.value}</span>;
                        }
                      } catch (parseError) {
                        // Continue to fallback
                      }
                    }

                    // Fallback for "Access App" column: use view-url annotation
                    if (
                      colDef.title.replace(/\s+/g, ' ').trim() === 'Access App'
                    ) {
                      const viewUrl =
                        actualEntity.metadata.annotations[
                          'backstage.io/view-url'
                        ];
                      if (viewUrl) {
                        return (
                          <AccessAppButton
                            entity={actualEntity}
                            url={viewUrl}
                            label="Access App"
                          />
                        );
                      }
                    }

                    return null;
                  } catch (error) {
                    return null;
                  }
                },
              });
            }
          });
        }
      } catch (e) {
        // If parsing fails, fall back to the simple App URL column
        customColumns.push({
          title: 'App URL',
          field: 'access-app-fallback',
          width: '140px',
          render: (rowData: CatalogTableRow) => {
            try {
              const actualEntity = getEntity(rowData);
              if (!actualEntity?.metadata?.annotations) {
                return null;
              }

              const isQuickProject =
                actualEntity.metadata.annotations[MICROAPP_ANNOTATION_KEY] ===
                MICROAPP_ANNOTATION_VALUE;
              if (!isQuickProject) {
                return null;
              }

              const viewUrl =
                actualEntity.metadata.annotations['backstage.io/view-url'];
              if (viewUrl) {
                return (
                  <AccessAppButton
                    entity={actualEntity}
                    url={viewUrl}
                    label="Access App"
                  />
                );
              }

              return null;
            } catch (error) {
              return null;
            }
          },
        });
      }
    }

    return [...updatedColumns, ...customColumns];
  };

/**
 * Custom columns function for microapps page with clickable app names and VPN indicators
 */
export const microappsColumnsFunc: CatalogTableColumnsFunc =
  entityListContext => {
    const columns = createColumnsFunc(['System', 'Lifecycle', 'Tags', 'Type'])(
      entityListContext,
    );

    // Find the Name/Title column index — in Backstage v1.33+ the default column
    // may be titled "Title" instead of "Name", and its field may be "resolved.entityRef"
    // or "resolved.name".
    const nameColumnIndex = columns.findIndex(col => {
      const title =
        typeof col.title === 'string' ? col.title.toLowerCase() : '';
      const field = typeof col.field === 'string' ? col.field : '';
      return (
        title === 'name' ||
        title === 'title' ||
        field === 'entity.metadata.name' ||
        field === 'resolved.entityRef' ||
        field === 'resolved.name'
      );
    });

    // Modify the Name/Title column to open app URL when clicked
    if (nameColumnIndex !== -1) {
      const originalNameColumn = columns[nameColumnIndex];
      columns[nameColumnIndex] = {
        ...originalNameColumn,
        render: (rowData: CatalogTableRow) => {
          try {
            const actualEntity = getEntity(rowData);

            if (!actualEntity?.metadata) {
              return originalNameColumn.render
                ? originalNameColumn.render(rowData, 'row')
                : null;
            }

            const appName =
              actualEntity.metadata.title ||
              actualEntity.metadata.name ||
              (rowData as any).resolved?.name ||
              '';
            const appUrl =
              actualEntity.metadata.annotations?.['backstage.io/view-url'];
            const requiresVPN =
              actualEntity.metadata.annotations?.['microapp/requiresVPN'] ===
              'true';
            const isRestricted =
              actualEntity.metadata.annotations?.['microapp/visibility'] ===
              'Custom (Specific Users)';

            // Shared badge for restricted-access apps
            const restrictedBadge = isRestricted ? (
              <Tooltip title="Access to this app is restricted to specific users.">
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    color: '#b26a00',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    padding: '3px 6px',
                    backgroundColor: 'rgba(178, 106, 0, 0.1)',
                    borderRadius: '4px',
                  }}
                >
                  <LockIcon style={{ fontSize: '12px', flexShrink: 0 }} />
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.3px',
                      textTransform: 'uppercase',
                      lineHeight: 1,
                    }}
                  >
                    Restricted
                  </span>
                </div>
              </Tooltip>
            ) : null;

            // If app URL exists, make the name clickable to open the app
            if (appUrl) {
              return (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {/* Opens production when the app has been promoted. */}
                  <MicroappLaunchUrl entity={actualEntity} url={appUrl}>
                    {href => (
                      <a
                        href={href ?? appUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#1976d2',
                          textDecoration: 'none',
                          fontWeight: 500,
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                        onFocus={e => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onBlur={e => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        {appName}
                      </a>
                    )}
                  </MicroappLaunchUrl>
                  {restrictedBadge}
                  {requiresVPN && (
                    <Tooltip title="This app requires VPN connection to access.">
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          color: '#228189',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          padding: '3px 6px',
                          backgroundColor: 'rgba(34, 129, 137, 0.1)',
                          borderRadius: '4px',
                        }}
                      >
                        <VpnLockIcon
                          style={{
                            color: '#228189',
                            fontSize: '12px',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            lineHeight: 1,
                          }}
                        >
                          VPN
                        </span>
                      </div>
                    </Tooltip>
                  )}
                </div>
              );
            }

            // Otherwise show the original name render with badges
            return (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span>
                  {originalNameColumn.render
                    ? originalNameColumn.render(rowData, 'row')
                    : appName}
                </span>
                {restrictedBadge}
                {requiresVPN && (
                  <Tooltip title="This app requires VPN connection to access.">
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        color: '#228189',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        padding: '3px 6px',
                        backgroundColor: 'rgba(34, 129, 137, 0.1)',
                        borderRadius: '4px',
                      }}
                    >
                      <VpnLockIcon
                        style={{
                          color: '#228189',
                          fontSize: '12px',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          letterSpacing: '0.3px',
                          textTransform: 'uppercase',
                          lineHeight: 1,
                        }}
                      >
                        VPN
                      </span>
                    </div>
                  </Tooltip>
                )}
              </div>
            );
          } catch (error) {
            return originalNameColumn.render
              ? originalNameColumn.render(rowData, 'row')
              : null;
          }
        },
      };
    }

    return columns.map(col => ({
      ...col,
      width: '30%',
    }));
  };
